import { Header } from './header/Header';
import { InputSection } from './input/InputSection';
import { PreviewSection } from './preview/PreviewSection';
import { SelectSection } from './select/SelectSection';
import { Footer } from './footer/Footer';
import { SelectionContext, useSelectionState } from 'src/contexts/SelectionContext';
import { EmbeddedContext } from 'src/contexts/EmbeddedContext';
import { useEmbeddedMode } from 'src/hooks/useEmbeddedMode';
import styles from './CampaignAutomationPage.module.css';
import { ClientContextProvider } from 'src/contexts/ClientContextProvider';
import { AssetContextProvider } from 'src/contexts/AssetContextProvider';
import { CampaignCreationSection } from './create/CampaignCreationSection';
import { CampaignMetadataContextProvider } from 'src/contexts/CampaignMetadataContextProvider';
import { PreviewSessionProvider } from './preview/devicePreview/PreviewSessionProvider';
import { DevicePreviewNavigationProvider } from './preview/devicePreview/DevicePreviewNavigationProvider';
import { useCallback, useEffect, useMemo } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { RDPProvider } from '@amzn/noolpaavai-react';
import { useAuthentication } from 'src/contexts/AuthenticationContext';
import { getUserAlias } from 'src/components/authentication/AuthenticationUtils';
import { getRdpStage } from 'src/configs/rdpConfiguration';
import { EmbeddedDoneButton } from './embedded/EmbeddedDoneButton';
import { metricsPublisher } from 'src/metrics';
import { DIMENSION_KEYS } from 'src/types';

const CampaignAutomationPage = () => {
  const { isEmbedded, embeddedPlacement, embeddedDeviceFamily, embeddedMarketplace } =
    useEmbeddedMode();
  const selectionState = useSelectionState(
    isEmbedded
      ? {
          marketplace: embeddedMarketplace,
          placement: embeddedPlacement,
          deviceFamily: embeddedDeviceFamily,
          skipPersist: true,
        }
      : undefined,
  );
  const { user } = useAuthentication();

  useEffect(() => {
    metricsPublisher.addGlobalDimensions({
      [DIMENSION_KEYS.MARKETPLACE]: selectionState.marketplace,
      [DIMENSION_KEYS.DEVICE_FAMILY]: selectionState.deviceFamily,
      [DIMENSION_KEYS.PLACEMENT]: selectionState.placement,
    });
  }, [selectionState.marketplace, selectionState.deviceFamily, selectionState.placement]);

  const getAuthToken = useCallback(
    async () => (await fetchAuthSession()).tokens?.idToken?.toString() ?? '',
    [],
  );

  const rdpConfig = useMemo(
    () => ({
      stage: getRdpStage(),
      getAuthToken,
      userId: user ? getUserAlias(user) : '',
    }),
    [getAuthToken, user],
  );

  const embeddedContextValue = useMemo(() => ({ isEmbedded }), [isEmbedded]);

  return (
    <EmbeddedContext.Provider value={embeddedContextValue}>
      <div className={styles.provider}>
        <ClientContextProvider>
          <SelectionContext.Provider value={selectionState}>
            <PreviewSessionProvider>
              <DevicePreviewNavigationProvider>
                <AssetContextProvider>
                  <main className={styles.page}>
                    <Header />
                    <SelectSection />
                    <CampaignMetadataContextProvider>
                      <div className={styles.mainContent}>
                        <div className={styles.leftColumn}>
                          <InputSection />
                          {isEmbedded && <EmbeddedDoneButton />}
                          {!isEmbedded && <CampaignCreationSection />}
                        </div>
                        <RDPProvider config={rdpConfig}>
                          <PreviewSection />
                        </RDPProvider>
                      </div>
                    </CampaignMetadataContextProvider>
                    {!isEmbedded && <Footer />}
                  </main>
                </AssetContextProvider>
              </DevicePreviewNavigationProvider>
            </PreviewSessionProvider>
          </SelectionContext.Provider>
        </ClientContextProvider>
      </div>
    </EmbeddedContext.Provider>
  );
};

export { CampaignAutomationPage };
